import React from "react";
import { useTranslation } from "react-i18next";
import { CardFooter, Spinner } from "reactstrap";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";

import { store as userStore } from "@components/VerticalLayout/store";
import {
    deviceStore,
    resetDeviceStore,
    setEditedSelectedRows,
    setIsPrinterModified,
    setIsAssociateConfirmOpened,
} from "../store";

import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../../api/hooks";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { associatePeripherals } from "../api";
import { TOAST_OPTIONS } from "../constants";
import {
    PERIPHERAL_TYPE_PRINTER,
    PERIPHERAL_TYPE_MONETIC,
    PERIPHERAL_TYPE_CASH_MANAGEMENT,
} from "@constants/index";

type AssociationRow = Record<string, unknown>;

type AssociationResult = { error?: boolean } | null;

type PrepheralCardFooterType = {
    dataIsUpdated: boolean | number;
    setIsEdited: (value: boolean) => void;
    setIsDeviceAssociateModalOpened: (value: boolean) => void;
    isDeviceAssociateModalOpened: boolean;
    rowData: AssociationRow[];
    setDataIsUpdated: (value: boolean | number) => void;
};

export default function PrepheralCardFooter({
    dataIsUpdated,
    setIsEdited,
    setIsDeviceAssociateModalOpened,
    isDeviceAssociateModalOpened,
    rowData,
    setDataIsUpdated,
}: PrepheralCardFooterType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const {
        cashManagements,
        monetics,
        printerSelected,
        isPrinterModified,
        editedSelectedRows,
    } = useSnapshot(deviceStore);

    const {
        userID,
        franchiseID,
        shopID,
        oneShop,
        operatorID,
    } = useSnapshot(userStore);

    // Locks the footer (and shows a spinner) while the association request is
    // in flight so the user cannot double-submit or cancel mid-request.
    const [isValidating, setIsValidating] = React.useState(false);

    // Whether the CURRENT selection actually differs from what the application
    // already had associated (rowData[0].subRows) — a real state comparison, not
    // the one-way "user clicked a select button" flag. So selecting a peripheral
    // then deselecting it back to the initial state counts as NO change.
    // Multi-application selection can't be compared to a single subRows set, so
    // it is always treated as a change.
    const selectionChanged = React.useMemo(() => {
        if (rowData.length > 1) return true;
        const initialAssociatedIds = new Set(
            (((rowData?.[0] as any)?.subRows as any[]) ?? [])
                .filter((row: any) =>
                    [
                        PERIPHERAL_TYPE_PRINTER,
                        PERIPHERAL_TYPE_MONETIC,
                        PERIPHERAL_TYPE_CASH_MANAGEMENT,
                    ].includes(row?.peripheralType)
                )
                .map((row: any) => row?.peripheralId)
        );
        const currentSelectedIds = new Set<unknown>([
            ...printerSelected.map((p: any) => p.Id),
            ...monetics.map((m: any) => m.Id),
            ...cashManagements.map((c: any) => c.Id),
        ]);
        if (currentSelectedIds.size !== initialAssociatedIds.size) return true;
        return Array.from(currentSelectedIds).some(
            (id) => !initialAssociatedIds.has(id)
        );
    }, [rowData, printerSelected, monetics, cashManagements]);

    async function handleValidateButtonClickEvent() {
        if (isValidating) return;
        const targetRows: AssociationRow[] =
            editedSelectedRows.length > 1
                ? [...(editedSelectedRows as AssociationRow[])]
                : [rowData[0]];

        const isMulti = targetRows.length > 1;

        if (isMulti && (monetics.length > 0 || cashManagements.length > 0)) {
            toast.warning(
                `${t(
                    "Attention, you cannot associate one payment system with more than one application."
                )}`,
                TOAST_OPTIONS
            );
            return;
        }

        setIsValidating(true);
        try {
            const results = await Promise.all(
                targetRows.map((row) =>
                    associatePeripherals(fetchGlobal, {
                        userID: userID.toString(),
                        franchiseID,
                        operatorID,
                        oneShop,
                        shopID: shopID.toString(),
                        rowData: [row],
                        printerSelected: [...printerSelected],
                        monetics: [...monetics],
                        cashManagements: [...cashManagements],
                        t,
                        isMulti,
                    })
                )
            );

            resetDeviceStore();
            setEditedSelectedRows([]);

            const allNull = results.every(
                (r): r is null => r === null
            );
            if (allNull) {
                setIsDeviceAssociateModalOpened(!isDeviceAssociateModalOpened);
                return;
            }

            const hasError = results.some(
                (r) => r !== null && (r as AssociationResult)?.error === true
            );
            if (hasError) {
                throw Error("Error while saving selection!");
            }

            setDataIsUpdated(!dataIsUpdated);
            setIsEdited(false);
            setIsDeviceAssociateModalOpened(!isDeviceAssociateModalOpened);

            toast.success(
                `${t("The association of this device was successful")!}`,
                {
                    ...TOAST_OPTIONS,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        } catch {
            resetDeviceStore();
            setEditedSelectedRows([]);
            toast.error(`${t("Mistake ! Please try again")}!`, TOAST_OPTIONS);
            setIsDeviceAssociateModalOpened(isDeviceAssociateModalOpened);
        } finally {
            setIsValidating(false);
        }
    }

    return (
        <div>
            <CardFooter className="Card-footer__clz">
                <div className="justify-content-between d-flex align-items-right  float-right p-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2"
                        variant="light"
                        disabled={isValidating}
                        onClick={() => {
                            if (isPrinterModified) {
                                setDataIsUpdated(!dataIsUpdated);
                            }
                            setIsDeviceAssociateModalOpened(
                                !isDeviceAssociateModalOpened
                            );
                            resetDeviceStore();
                            setEditedSelectedRows([]);
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        variant="primary"
                        disabled={isValidating}
                        aria-label={t("Validate")}
                        onClick={() => {
                            if (selectionChanged) {
                                handleValidateButtonClickEvent();
                            } else {
                                setIsAssociateConfirmOpened(true);
                            }
                            setIsPrinterModified(false);
                        }}
                        className="afa_btn_validatePopup"
                    >
                        {isValidating ? (
                            <Spinner size="sm" />
                        ) : (
                            t("Validate")
                        )}
                    </CustomMainColorButton>
                </div>
            </CardFooter>
        </div>
    );
}
